import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Badge
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  ViewWeek as WeekIcon,
  ViewDay as DayIcon,
  ViewModule as MonthIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const localizer = momentLocalizer(moment);

// Custom styles for different timesheet statuses
const eventStyleGetter = (event) => {
  const status = event.resource?.status || 'pending';
  let backgroundColor, color, borderColor;
  
  switch (status) {
    case 'approved':
      backgroundColor = '#4caf50';
      color = 'white';
      borderColor = '#2e7d32';
      break;
    case 'pending':
      backgroundColor = '#ff9800';
      color = 'white';
      borderColor = '#f57c00';
      break;
    case 'rejected':
      backgroundColor = '#f44336';
      color = 'white';
      borderColor = '#d32f2f';
      break;
    case 'in_progress':
      backgroundColor = '#2196f3';
      color = 'white';
      borderColor = '#1976d2';
      break;
    case 'holiday':
      backgroundColor = '#9c27b0';
      color = 'white';
      borderColor = '#7b1fa2';
      break;
    case 'sick':
      backgroundColor = '#ff5722';
      color = 'white';
      borderColor = '#e64a19';
      break;
    case 'weekend':
      backgroundColor = '#e0e0e0';
      color = '#424242';
      borderColor = '#bdbdbd';
      break;
    default:
      backgroundColor = '#757575';
      color = 'white';
      borderColor = '#616161';
  }

  return {
    style: {
      backgroundColor,
      color,
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: '4px',
      opacity: event.resource?.is_complete ? 0.7 : 1,
      border: 'none',
      display: 'block',
      fontSize: '12px',
      fontWeight: 'bold'
    }
  };
};

const TimesheetCalendar = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [events, setEvents] = useState([]);
  
  // Filter state
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProject, setSelectedProject] = useState('');
  
  // Modal state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [dayDetailsOpen, setDayDetailsOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  
  // Resources
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  
  // Statistics
  const [stats, setStats] = useState({});

  // ====================================
  // DATA FETCHING
  // ====================================

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    fetchTimesheetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, view, selectedEmployee, selectedStatus, selectedProject]);

  const fetchResources = async () => {
    try {
      // Fetch employees
      const employeesResponse = await makeApiCall('api/employees?page=1&limit=1000&is_active=true');
      if (employeesResponse.ok) {
        const employeesData = await employeesResponse.json();
        setEmployees(employeesData.employees || []);
      }

      // Fetch projects (if you have a projects API)
      // const projectsResponse = await makeApiCall('api/projects');
      // if (projectsResponse.ok) {
      //   const projectsData = await projectsResponse.json();
      //   setProjects(projectsData.projects || []);
      // }
    } catch (err) {
      console.error('Error fetching resources:', err);
    }
  };

  // TKT-572: the calendar never told the server which employee to show, so
  // the server fell back to the logged-in user's own hours: every name in
  // the list showed Naomi's 218.6h for her, and 0.0h for Glen. It now asks
  // for the picked employee, or everyone ("all") when none is picked, and
  // works out the Monthly Summary from the same entries it draws.
  const fetchTimesheetData = async () => {
    try {
      setLoading(true);
      setError('');
      const startDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
      const employeeParam = encodeURIComponent(selectedEmployee || 'all');

      const response = await makeApiCall(
        `api/time-tracking/calendar?start_date=${startDate}&end_date=${endDate}&employee_id=${employeeParam}`
      );

      if (response.ok) {
        const data = await response.json();
        const days = filterCalendarData(data.data || {});
        setEvents(convertToCalendarEvents(days));
        setStats(summarize(days));
      } else {
        // Used to fall back to made-up sample timesheets, which looked real.
        setEvents([]);
        setStats({});
        setError('Failed to load timesheet data');
      }
    } catch (err) {
      console.error('Error fetching timesheet data:', err);
      setError('Failed to load timesheet data');
      setEvents([]);
      setStats({});
    } finally {
      setLoading(false);
    }
  };

  const entryStatus = (entry) => (!entry.clock_out_time ? 'in_progress' : (entry.approval_status || 'pending'));

  // Status / Project filters apply to the entries shown and to the summary.
  const filterCalendarData = (apiData) => {
    const out = {};
    Object.entries(apiData).forEach(([dateKey, dayData]) => {
      const entries = (dayData.entries || []).filter((entry) => (
        (selectedStatus === 'all' || entryStatus(entry) === selectedStatus)
        && (!selectedProject || entry.project_code === selectedProject)
      ));
      if (entries.length > 0) out[dateKey] = { ...dayData, entries };
    });
    return out;
  };

  const summarize = (days) => {
    const all = Object.values(days).flatMap((day) => day.entries);
    const sum = (key) => all.reduce((total, entry) => total + (parseFloat(entry[key]) || 0), 0);
    return {
      total_hours: sum('hours_worked'),
      total_overtime: sum('overtime_hours'),
      total_days: Object.keys(days).length,
      pending_days: Object.values(days).filter((day) => day.entries.some((entry) => entryStatus(entry) === 'pending')).length
    };
  };

  const convertToCalendarEvents = (apiData) => {
    const events = [];
    Object.entries(apiData).forEach(([dateKey, dayData]) => {
      if (dayData.entries && dayData.entries.length > 0) {
        dayData.entries.forEach((entry, index) => {
          const startTime = new Date(entry.clock_in_time);
          const endTime = entry.clock_out_time ? new Date(entry.clock_out_time) : new Date(startTime.getTime() + 8 * 60 * 60 * 1000);
          
          let title = `${entry.hours_worked || 0}h`;
          if (!selectedEmployee && entry.employee_name) title = `${entry.employee_name}: ${title}`;
          if (entry.project_code) title += ` - ${entry.project_code}`;
          if (entry.overtime_hours > 0) title += ` (+${entry.overtime_hours}h OT)`;
          if (entry.approval_status === 'pending') title += ' (Pending)';
          if (!entry.clock_out_time) title += ' (In Progress)';
          
          events.push({
            id: entry.id,
            title,
            start: startTime,
            end: endTime,
            resource: {
              ...entry,
              status: entry.approval_status || 'pending',
              totalHours: entry.hours_worked || 0,
              regularHours: entry.regular_hours || 0,
              overtimeHours: entry.overtime_hours || 0,
              employeeName: entry.employee_name,
              clockIn: entry.clock_in_time ? moment(entry.clock_in_time).format('HH:mm') : null,
              clockOut: entry.clock_out_time ? moment(entry.clock_out_time).format('HH:mm') : null
            }
          });
        });
      } else if (dayData.status === 'holiday' || dayData.status === 'sick' || dayData.status === 'weekend') {
        const date = new Date(dateKey);
        events.push({
          id: `special-${dateKey}`,
          title: dayData.status === 'holiday' ? 'Holiday' : 
                 dayData.status === 'sick' ? 'Sick Leave' : 'Weekend',
          start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0),
          end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59),
          resource: {
            status: dayData.status,
            totalHours: 0,
            regularHours: 0,
            overtimeHours: 0
          }
        });
      }
    });
    return events;
  };

  // ====================================
  // EVENT HANDLERS
  // ====================================

  const handleNavigate = (date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedDayData({
      start: slotInfo.start,
      end: slotInfo.end,
      resource: {
        status: 'new',
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0
      }
    });
    setDayDetailsOpen(true);
  };

  const handleRefresh = () => {
    fetchTimesheetData();
  };

  // ====================================
  // RENDER METHODS
  // ====================================

  const renderToolbar = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
            HR Timesheet Calendar
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {/* View Controls */}
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, newView) => newView && handleViewChange(newView)}
              size="small"
            >
              <ToggleButton value="month">
                <MonthIcon sx={{ mr: 1 }} />
                Month
              </ToggleButton>
              <ToggleButton value="week">
                <WeekIcon sx={{ mr: 1 }} />
                Week
              </ToggleButton>
              <ToggleButton value="day">
                <DayIcon sx={{ mr: 1 }} />
                Day
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Navigation Controls */}
            <ButtonGroup size="small">
              <IconButton onClick={() => handleNavigate(moment(currentDate).subtract(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day').toDate())}>
                <PrevIcon />
              </IconButton>
              <IconButton onClick={() => handleNavigate(new Date())}>
                <TodayIcon />
              </IconButton>
              <IconButton onClick={() => handleNavigate(moment(currentDate).add(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day').toDate())}>
                <NextIcon />
              </IconButton>
            </ButtonGroup>

            <Divider orientation="vertical" flexItem />

            {/* Action Buttons */}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              size="small"
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              label="Employee"
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.employee_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Project"
            >
              <MenuItem value="">All Projects</MenuItem>
              <MenuItem value="PROJ-001">PROJ-001</MenuItem>
              <MenuItem value="PROJ-002">PROJ-002</MenuItem>
              <MenuItem value="PROJ-003">PROJ-003</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );

  const renderStats = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Monthly Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" fontWeight="bold">
                {stats.total_hours?.toFixed(1) || '0.0'}h
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total Hours
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="error" fontWeight="bold">
                {stats.total_overtime?.toFixed(1) || '0.0'}h
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Overtime
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="text.primary" fontWeight="bold">
                {stats.total_days || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Work Days
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {stats.pending_days || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Pending
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderEventDetailsDialog = () => (
    <Dialog open={eventDetailsOpen} onClose={() => setEventDetailsOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Timesheet Entry Details
          </Typography>
          <Chip
            label={selectedEvent?.resource?.status?.toUpperCase() || 'UNKNOWN'}
            color={selectedEvent?.resource?.status === 'approved' ? 'success' : 
                   selectedEvent?.resource?.status === 'pending' ? 'warning' : 
                   selectedEvent?.resource?.status === 'rejected' ? 'error' : 'default'}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedEvent && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                <Typography variant="body1">{selectedEvent.resource?.employeeName || 'N/A'}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                <Typography variant="body1">{moment(selectedEvent.start).format('MMM D, YYYY')}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Clock In</Typography>
                <Typography variant="body1">
                  {selectedEvent.resource?.clockIn || 
                   (selectedEvent.start ? moment(selectedEvent.start).format('HH:mm') : 'N/A')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Clock Out</Typography>
                <Typography variant="body1">
                  {selectedEvent.resource?.clockOut || 
                   (selectedEvent.end ? moment(selectedEvent.end).format('HH:mm') : 'N/A')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Total Hours</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedEvent.resource?.totalHours || 0}h</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Overtime</Typography>
                <Typography variant="body1" color="error">{selectedEvent.resource?.overtimeHours || 0}h</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Project Code</Typography>
                <Typography variant="body1">{selectedEvent.resource?.projectCode || 'N/A'}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">Cost Center</Typography>
                <Typography variant="body1">{selectedEvent.resource?.costCenter || 'N/A'}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                <Typography variant="body1">{selectedEvent.resource?.notes || 'No notes'}</Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEventDetailsOpen(false)}>Close</Button>
        <Button variant="contained">Edit Entry</Button>
      </DialogActions>
    </Dialog>
  );

  const renderDayDetailsDialog = () => (
    <Dialog open={dayDetailsOpen} onClose={() => setDayDetailsOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Add Timesheet Entry - {moment(selectedDayData?.start).format('MMM D, YYYY')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Clock In Time"
                type="time"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Clock Out Time"
                type="time"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Break Duration (minutes)"
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Lunch Duration (minutes)"
                type="number"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Project Code"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Cost Center"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Notes"
                multiline
                rows={3}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDayDetailsOpen(false)}>Cancel</Button>
        <Button variant="contained">Save Entry</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderToolbar()}
      {renderStats()}

      <Paper sx={{ p: 2, height: 'calc(100vh - 400px)', minHeight: 500 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          view={view}
          date={currentDate}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          popup
          eventPropGetter={eventStyleGetter}
          views={['month', 'week', 'day', 'agenda']}
          step={30}
          showMultiDayTimes
          defaultDate={new Date()}
          style={{ height: '100%' }}
          components={{
            event: ({ event }) => (
              <div style={{ fontSize: '11px', fontWeight: 'bold', padding: '2px' }}>
                {event.title}
              </div>
            )
          }}
        />
      </Paper>

      {renderEventDetailsDialog()}
      {renderDayDetailsDialog()}
    </Box>
  );
};

export default TimesheetCalendar;